import clsx from "clsx";
import ReactTimeago from "react-timeago";
import { Fragment, useContext, useEffect, useState } from "react";
import { Page } from "./Page";
import { RouteLink, RouteList } from "Routes";
import { GlobalContext } from "contexts/GlobalContext";
import { GetLaddersResult } from "results/ladders/GetLaddersResult";
import { ListGroupNameDescription } from "components/list-groups/ListGroupNameDescription";
import { Link } from "react-router-dom";
import { TwoThirdsListPlaceholder } from "placeholder/ListPlaceholder";
import { DataContent } from "components/DataContent";
import { BrandingUtils } from "utils/BrandingUtils";

const HomePage = () => {
    const { siteName } = BrandingUtils;

    const { service, catchServerError, getGame } = useContext(GlobalContext);
    const { getLadders } = service;

    const [data, setData] = useState<GetLaddersResult>();

    useEffect(() => {
        let mounted = true;

        getLadders()
            .then(x => {
                if (mounted) {
                    setData(x);
                }
            })
            .catch(catchServerError);

        return () => {
            mounted = false;
        };
    }, [catchServerError, getLadders]);

    return (
        <Page title={`${siteName} ladders`} description={`${siteName} ladders`} canonical={RouteList.Home}>
            <DataContent data={data} placeholder={<TwoThirdsListPlaceholder />}>
                {data && (
                    <Fragment>
                        <div className="g-col-12 g-col-lg-8">
                            <div className="list-group">
                                {data.ladders.map(l => {
                                    const game = getGame(l.gameId);

                                    // closed ladders should go to the leaderboard not the lobby
                                    const url = l.isClosed
                                        ? RouteLink.ladderLeaderboard(l.id)
                                        : RouteLink.ladderLobby(l.id);

                                    return (
                                        <Link
                                            key={l.id}
                                            to={url}
                                            className={clsx(
                                                "list-group-item list-group-item-action",
                                                l.isQueueOpen
                                                    ? "list-group-item-primary"
                                                    : l.isRegistrationOpen
                                                    ? "list-group-item-secondary"
                                                    : "",
                                            )}
                                        >
                                            <div className="hstack gap-3">
                                                <div className="d-none d-lg-block">
                                                    <img alt="" src={game?.imageUrl} />
                                                </div>

                                                <ListGroupNameDescription
                                                    name={l.name}
                                                    description={
                                                        <Fragment>
                                                            {l.isQueueOpen ? (
                                                                <Fragment>
                                                                    Ends in <ReactTimeago date={new Date(l.endDate)} />
                                                                </Fragment>
                                                            ) : l.isRegistrationOpen ? (
                                                                <Fragment>
                                                                    Opens in{" "}
                                                                    <ReactTimeago date={new Date(l.startDate)} />
                                                                </Fragment>
                                                            ) : l.isClosed ? (
                                                                <Fragment>
                                                                    Ended <ReactTimeago date={new Date(l.endDate)} />
                                                                </Fragment>
                                                            ) : (
                                                                <Fragment>
                                                                    Opens in{" "}
                                                                    <ReactTimeago date={new Date(l.startDate)} />
                                                                </Fragment>
                                                            )}
                                                        </Fragment>
                                                    }
                                                />

                                                <div>
                                                    {l.isQueueOpen ? (
                                                        <span className="badge bg-success rounded-pill">
                                                            Queue open
                                                        </span>
                                                    ) : l.isRegistrationOpen ? (
                                                        <span className="badge bg-primary rounded-pill">
                                                            Registrations open
                                                        </span>
                                                    ) : (
                                                        <Fragment></Fragment>
                                                    )}
                                                </div>

                                                <div style={{ width: 45 }} className="text-end text-nowrap small">
                                                    {l.entrantCount} <span className="fa-solid fa-user"></span>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="g-col-12 g-col-lg-4">
                            <div className="alert alert-primary">
                                <div className="vstack gap-3">
                                    <h2 className="h5">About the ladders</h2>

                                    <ul className="mb-0">
                                        <li>Queue for matches or challenge opponents.</li>
                                        <li>Earn Elo by beating opponents.</li>
                                        <li>Online or offline for any Smash Bros. game.</li>
                                        <li>
                                            Requires an{" "}
                                            <a
                                                href="https://ausmash.com.au"
                                                className="alert-link"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {BrandingUtils.siteName}
                                            </a>{" "}
                                            account to participate.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
            </DataContent>
        </Page>
    );
};

export default HomePage;
